import { useState } from 'react';
import {
  Archive,
  CheckCircle2,
  ChevronDown,
  Download,
  Loader2,
  AlertTriangle,
  X,
} from 'lucide-react';
import { useDownloadStore } from '../../store/downloadStore';
import { CorruptPhotosModal } from '../Albums/CorruptPhotosModal';
import styles from './DownloadPanel.module.css';

/**
 * The one visible surface for a client-side ZIP download.
 *
 * Mounted once at the app root so a download survives navigation. Shows real
 * byte progress rather than a spinner, because the browser now owns the whole
 * pipeline and actually knows how far along it is.
 */

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  const v = n / 1024 ** i;
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

function formatEta(seconds: number | null): string | null {
  if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) return null;
  if (seconds < 60) return `${Math.round(seconds)}s left`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min left`;
  const h = Math.floor(m / 60);
  return `${h} h ${m % 60} m left`;
}

export function DownloadPanel() {
  const progress = useDownloadStore((s) => s.progress);
  const minimized = useDownloadStore((s) => s.minimized);
  const setMinimized = useDownloadStore((s) => s.setMinimized);
  const cancel = useDownloadStore((s) => s.cancel);
  const dismiss = useDownloadStore((s) => s.dismiss);
  const savePart = useDownloadStore((s) => s.savePart);
  const saveAll = useDownloadStore((s) => s.saveAll);
  const openPart = useDownloadStore((s) => s.openPart);
  const retrySkipped = useDownloadStore((s) => s.retrySkipped);
  const [skippedOpen, setSkippedOpen] = useState(false);

  if (!progress) return null;

  const {
    phase,
    albumName,
    bytesDone,
    bytesTotal,
    filesDone,
    filesTotal,
    partIndex,
    partCount,
    currentFile,
    bytesPerSec,
    etaSeconds,
    skipped,
    pendingSaves,
    openable,
    savedTo,
    estimated,
    error,
  } = progress;

  const running = phase === 'planning' || phase === 'downloading' || phase === 'awaiting-save';
  /** Finished, but the bytes are still staged — the user hasn't chosen a home yet. */
  const awaitingSave = pendingSaves.length > 0;
  const pct = bytesTotal > 0 ? Math.min(100, (bytesDone / bytesTotal) * 100) : 0;
  const approx = estimated ? '~' : '';

  if (minimized && running) {
    return (
      <button className={styles['pill']} onClick={() => setMinimized(false)} type="button">
        <Loader2 className={styles['spin']} size={15} aria-hidden />
        {Math.round(pct)}% · {albumName}
      </button>
    );
  }

  const fillClass = [
    styles['fill'],
    phase === 'done' ? styles['fill-done'] : '',
    phase === 'failed' ? styles['fill-failed'] : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div className={styles['panel']} role="status" aria-live="polite">
        <div className={styles['header']}>
          <span className={styles['title']}>
            {phase === 'done' ? (
              <CheckCircle2 size={16} color="#22c55e" aria-hidden />
            ) : phase === 'failed' ? (
              <AlertTriangle size={16} color="#ef4444" aria-hidden />
            ) : (
              <Archive size={16} aria-hidden />
            )}
            <span className={styles['title-text']}>{albumName}</span>
          </span>
          <span style={{ display: 'inline-flex', gap: '0.125rem' }}>
            {running && (
              <button
                className={styles['icon-btn']}
                onClick={() => setMinimized(true)}
                aria-label="Minimize download"
                type="button"
              >
                <ChevronDown size={16} />
              </button>
            )}
            {!running && (
              <button
                className={styles['icon-btn']}
                onClick={dismiss}
                aria-label="Dismiss"
                type="button"
              >
                <X size={16} />
              </button>
            )}
          </span>
        </div>

        {/* Multi-part downloads get a segment per part so the shape of the job
            is visible at a glance; single-part gets one continuous bar. */}
        {partCount > 1 ? (
          <div className={styles['segments']} aria-hidden>
            {Array.from({ length: partCount }, (_, i) => (
              <div
                key={i}
                className={`${styles['segment']} ${i < partIndex ? styles['segment-done'] : ''}`}
              >
                {i === partIndex && (
                  <div className={fillClass} style={{ transform: `scaleX(${pct / 100})` }} />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div
            className={`${styles['track']} ${phase === 'planning' ? styles['track-indeterminate'] : ''}`}
            role="progressbar"
            aria-valuenow={phase === 'planning' ? undefined : Math.round(pct)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Download progress"
          >
            <div
              className={fillClass}
              style={phase === 'planning' ? undefined : { transform: `scaleX(${pct / 100})` }}
            />
          </div>
        )}

        {phase === 'planning' && <div className={styles['primary']}>Preparing your download…</div>}

        {(phase === 'downloading' || phase === 'awaiting-save') && (
          <>
            <div className={styles['primary']}>
              {filesDone.toLocaleString()} of {filesTotal.toLocaleString()} photos ·{' '}
              {formatBytes(bytesDone)} of {approx}
              {formatBytes(bytesTotal)} · {Math.round(pct)}%
            </div>
            <div className={styles['secondary']}>
              {partCount > 1 && <span>Part {partIndex + 1} of {partCount}</span>}
              {currentFile && <span className={styles['secondary-file']}>{currentFile}</span>}
            </div>
            <div className={styles['secondary']}>
              {bytesPerSec > 0 && <span>{formatBytes(bytesPerSec)}/s</span>}
              {formatEta(etaSeconds) && <span>· {formatEta(etaSeconds)}</span>}
            </div>
          </>
        )}

        {phase === 'done' && (
          <>
            {/* The archive is staged, not on the user's disk yet — saying
                "Downloaded" here would promise a file they don't have. Only the
                service-worker path, which really did hand the file to the
                browser, gets to say that. */}
            <div className={styles['primary']}>
              {awaitingSave
                ? `${filesDone.toLocaleString()} photo${filesDone !== 1 ? 's' : ''} ready · ${formatBytes(bytesDone)}`
                : savedTo
                  ? `Saved ${filesDone.toLocaleString()} photo${filesDone !== 1 ? 's' : ''} · ${formatBytes(bytesDone)}`
                  : `Downloaded ${filesDone.toLocaleString()} photo${filesDone !== 1 ? 's' : ''} · ${formatBytes(bytesDone)}`}
              {partCount > 1 && ` · ${partCount} files`}
            </div>

            <div className={styles['secondary']}>
              {awaitingSave ? (
                <span className={styles['secondary-file']}>
                  {pendingSaves.length > 1
                    ? 'Pick a folder to save them into'
                    : 'Pick where to save it'}
                </span>
              ) : savedTo ? (
                <span className={styles['secondary-file']}>
                  {savedTo.kind === 'folder' ? 'Saved to folder ' : 'Saved as '}
                  <strong>{savedTo.name}</strong>
                </span>
              ) : (
                <span className={styles['secondary-file']}>Saved to your downloads</span>
              )}
            </div>
          </>
        )}

        {phase === 'cancelled' && <div className={styles['primary']}>Download cancelled</div>}

        {phase === 'failed' && (
          <div className={styles['error']}>{error ?? 'The download could not be completed.'}</div>
        )}

        {/* Only memory-held parts are urgent; staged ones sit on disk. */}
        {phase === 'awaiting-save' && pendingSaves.some((p) => p.inMemory) && (
          <div className={styles['warn']}>
            Save the finished part{pendingSaves.length > 1 ? 's' : ''} to continue — they're held
            in memory until you do.
          </div>
        )}

        {skipped.length > 0 && phase !== 'downloading' && (
          <div className={styles['warn']}>
            {skipped.length} photo{skipped.length !== 1 ? 's' : ''} could not be downloaded.
          </div>
        )}

        {/* One row for every action — Save and Close previously lived in two
            separate containers and stacked into two ragged rows. */}
        <div className={styles['actions']}>
          {/* The picker cannot be opened for the user: showSaveFilePicker needs
              transient user activation, so a click is mandatory — it would be
              rejected if fired automatically when the download finished. */}
          {awaitingSave && pendingSaves.length === 1 && (
            <button
              className={`${styles['btn']} ${styles['btn-primary']}`}
              onClick={() => void savePart(pendingSaves[0].partIndex)}
              type="button"
              title={`Choose where to save ${pendingSaves[0].name}`}
            >
              <Download size={13} aria-hidden />
              Save to your device
            </button>
          )}

          {awaitingSave && pendingSaves.length > 1 && (
            <>
              <button
                className={`${styles['btn']} ${styles['btn-primary']}`}
                onClick={() => void saveAll()}
                type="button"
                title="Choose one folder for every part"
              >
                <Download size={13} aria-hidden />
                Save all {pendingSaves.length} parts
              </button>
              {pendingSaves.map((p) => (
                <button
                  key={p.partIndex}
                  className={`${styles['btn']} ${styles['btn-ghost']}`}
                  onClick={() => void savePart(p.partIndex)}
                  type="button"
                  title={`Choose where to save ${p.name}`}
                >
                  Part {p.partIndex + 1}
                </button>
              ))}
            </>
          )}

          {running && (
            <button
              className={`${styles['btn']} ${styles['btn-ghost']}`}
              onClick={cancel}
              type="button"
            >
              Cancel
            </button>
          )}

          {/* A page cannot reveal a file in Finder/Explorer, so this hands the
              bytes back to the browser rather than pretending to open it. */}
          {phase === 'done' &&
            !awaitingSave &&
            openable.map((o) => (
              <button
                key={o.partIndex}
                className={`${styles['btn']} ${styles['btn-ghost']}`}
                onClick={() => openPart(o.partIndex)}
                type="button"
                title={`Save another copy of ${o.name}`}
              >
                <Download size={13} aria-hidden />
                {openable.length > 1 ? `Save part ${o.partIndex + 1} again` : 'Save again'}
              </button>
            ))}

          {!running && skipped.length > 0 && (
            <>
              <button
                className={`${styles['btn']} ${styles['btn-ghost']}`}
                onClick={() => void retrySkipped()}
                type="button"
              >
                Retry {skipped.length} failed
              </button>
              <button
                className={`${styles['btn']} ${styles['btn-ghost']}`}
                onClick={() => setSkippedOpen(true)}
                type="button"
              >
                Show skipped
              </button>
            </>
          )}

          {!running && (
            <button
              className={`${styles['btn']} ${styles['btn-ghost']}`}
              onClick={dismiss}
              type="button"
            >
              Close
            </button>
          )}
        </div>
      </div>

      <CorruptPhotosModal
        isOpen={skippedOpen}
        onClose={() => setSkippedOpen(false)}
        photos={skipped.map((s) => ({ photoId: s.photoId, filename: s.name }))}
        refixing={false}
        onRefix={() => setSkippedOpen(false)}
      />
    </>
  );
}
